import type { WordPack } from '../content/packs';
import { dictionaryVersion, normalizeWord, wordCells } from './language';
import { challengeVersion, isChallenge, type Challenge } from './challenge';

export const directions: ReadonlyArray<readonly [number, number]> = [
  [0, 1], [0, -1], [1, 0], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1],
];
export interface Target { word: string; cells: number[] }
export interface Board {
  id: string;
  seed: string;
  rows: number;
  columns: number;
  letters: string[];
  targets: Target[];
  packId: string;
  packVersion: string;
  locale: string;
  dictionaryVersion: string;
  challenge?: Challenge;
  challengeVersion?: string;
}
export interface GenerateOptions {
  rows: number;
  columns: number;
  count: number;
  seed: string;
  pack: WordPack;
  blocked?: string[];
  deadlineMs?: number;
  challenge?: Challenge;
}

export function seededRandom(seed: string): () => number {
  let state = 2166136261;
  for (const char of seed) state = Math.imul(state ^ char.codePointAt(0)!, 16777619);
  return () => {
    state += 0x6D2B79F5;
    let t = Math.imul(state ^ (state >>> 15), state | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function lineCells(rows: number, columns: number, start: number, end: number): number[] | null {
  if (![rows, columns, start, end].every(Number.isInteger) || rows < 1 || columns < 1 || start < 0 || end < 0 || start >= rows * columns || end >= rows * columns) return null;
  const r1 = Math.floor(start / columns), c1 = start % columns;
  const r2 = Math.floor(end / columns), c2 = end % columns;
  const dr = r2 - r1, dc = c2 - c1;
  if (dr !== 0 && dc !== 0 && Math.abs(dr) !== Math.abs(dc)) return null;
  const length = Math.max(Math.abs(dr), Math.abs(dc)) + 1;
  if (length < 3) return null;
  return Array.from({ length }, (_, step) => (r1 + step * Math.sign(dr)) * columns + c1 + step * Math.sign(dc));
}

export function canonicalPath(cells: number[]): string {
  return [cells[0], cells.at(-1)].sort((a, b) => a! - b!).join(':');
}

function shuffled<T>(items: T[], random: () => number): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j]!, result[i]!];
  }
  return result;
}

interface BlockedNode { children: Map<string, BlockedNode>; terminal: boolean }
function blockedTrie(words: string[], locale: string): BlockedNode {
  const root: BlockedNode = { children: new Map(), terminal: false };
  for (const word of words) {
    const glyphs = [...new Intl.Segmenter(locale, { granularity: 'grapheme' }).segment(word.normalize('NFC').toLocaleUpperCase(locale))].map(part => part.segment);
    if (glyphs.length < 3) continue;
    let node = root;
    for (const glyph of glyphs) {
      if (!node.children.has(glyph)) node.children.set(glyph, { children: new Map(), terminal: false });
      node = node.children.get(glyph)!;
    }
    node.terminal = true;
  }
  return root;
}
function blockedPath(board: Board, root: BlockedNode): number[] | null {
  for (let start = 0; start < board.letters.length; start++) for (const [dr, dc] of directions) {
    let node = root;
    const cells: number[] = [];
    let row = Math.floor(start / board.columns), col = start % board.columns;
    while (row >= 0 && row < board.rows && col >= 0 && col < board.columns) {
      const cell = row * board.columns + col;
      const child = node.children.get(board.letters[cell]!);
      if (!child) break;
      cells.push(cell); node = child;
      if (node.terminal) return cells;
      row += dr; col += dc;
    }
  }
  return null;
}
export function containsBlocked(board: Board, blocked: string[]): boolean { return blockedPath(board, blockedTrie(blocked, board.locale)) !== null; }

export function generateBoard(options: GenerateOptions): Board {
  const { rows, columns, pack, seed } = options;
  if (!Number.isInteger(rows) || !Number.isInteger(columns) || rows < 3 || columns < 3 || rows > 32 || columns > 32 || rows * columns > 768) throw new Error('Unsupported board dimensions');
  if (!Number.isFinite(options.count) || options.count < 1 || pack.language.alphabet.length === 0) throw new Error('Invalid generation request');
  if (options.challenge !== undefined && !isChallenge(options.challenge)) throw new Error('Unknown challenge');
  const placementDirections = options.challenge === 'relaxed' ? [[0, 1], [1, 0], [1, 1]] as const
    : options.challenge === 'challenging' ? [...directions, ...directions.filter(([dr, dc]) => dr < 0 || dc < 0)] : directions;
  const started = Date.now();
  const blocked = new Set((options.blocked ?? []).map(word => normalizeWord(word, pack.language)));
  const trie = blockedTrie([...blocked], pack.language.locale);
  const candidates = [...new Set(pack.words.map(word => normalizeWord(word, pack.language)))].filter(word => {
    const letters = wordCells(word, pack.language);
    if (letters === null || letters.length > Math.max(rows, columns) || blocked.has(word)) return false;
    // A permitted target must not force an unavoidable blocked substring in either direction.
    for (let start = 0; start < letters.length; start++) for (let end = start + 3; end <= letters.length; end++) {
      const part = letters.slice(start, end);
      if (blocked.has(part.join('')) || blocked.has([...part].reverse().join(''))) return false;
    }
    return true;
  });
  if (candidates.length === 0) throw new Error('No words fit this board');
  const count = Math.min(Math.floor(options.count), candidates.length, 60);
  for (let generation = 0; generation < 8; generation++) {
    const random = seededRandom(`${seed}:${generation}`);
    const letters = new Array<string>(rows * columns).fill('');
    const targets: Target[] = [];
    for (const word of shuffled(candidates, random)) {
      if (Date.now() - started > (options.deadlineMs ?? 2000)) throw new Error('Board generation deadline exceeded');
      if (targets.length >= count) break;
      const glyphs = wordCells(word, pack.language)!;
      let placement: number[] | undefined, bestOverlap = -1, validPlacements = 0;
      for (let attempt = 0; attempt < 80; attempt++) {
        const [dr, dc] = placementDirections[Math.floor(random() * placementDirections.length)]!;
        const rowMin = dr < 0 ? glyphs.length - 1 : 0;
        const colMin = dc < 0 ? glyphs.length - 1 : 0;
        const rowMax = rows - 1 - (dr > 0 ? glyphs.length - 1 : 0);
        const colMax = columns - 1 - (dc > 0 ? glyphs.length - 1 : 0);
        if (rowMin > rowMax || colMin > colMax) continue;
        const row = rowMin + Math.floor(random() * (rowMax - rowMin + 1));
        const col = colMin + Math.floor(random() * (colMax - colMin + 1));
        const cells = glyphs.map((_, i) => (row + dr * i) * columns + col + dc * i);
        if (!cells.every((cell, i) => letters[cell] === '' || letters[cell] === glyphs[i])) continue;
        const overlap = cells.filter(cell => letters[cell] !== '').length;
        if (overlap > bestOverlap) { bestOverlap = overlap; placement = cells; }
        validPlacements++;
        if (options.challenge !== 'challenging' || overlap >= Math.ceil(glyphs.length / 3) || validPlacements >= 8) break;
      }
      if (placement) {
        placement.forEach((cell, i) => { letters[cell] = glyphs[i]!; });
        targets.push({ word, cells: placement });
      }
    }
    if (targets.length === 0) continue;
    for (let i = 0; i < letters.length; i++) if (!letters[i]) letters[i] = pack.language.alphabet[Math.floor(random() * pack.language.alphabet.length)]!;
    const board: Board = {
      id: `${pack.id}:${rows}x${columns}:${seed}`, seed, rows, columns, letters,
      targets: targets.sort((a, b) => a.word.localeCompare(b.word, pack.language.locale)),
      packId: pack.id, packVersion: pack.version, locale: pack.language.locale, dictionaryVersion: dictionaryVersion(pack.language.locale),
      ...(options.challenge ? { challenge: options.challenge, challengeVersion } : {}),
    };
    const fixed = new Set(targets.flatMap(target => target.cells));
    // Repair only filler cells; placed words and scoreable target paths remain unchanged.
    for (let repair = 0; repair < 300; repair++) {
      if (Date.now() - started > (options.deadlineMs ?? 2000)) throw new Error('Board generation deadline exceeded');
      const path = blockedPath(board, trie);
      if (!path) return board;
      const editable = path.filter(cell => !fixed.has(cell));
      if (editable.length === 0) break;
      const cell = editable[Math.floor(random() * editable.length)]!;
      board.letters[cell] = pack.language.alphabet[Math.floor(random() * pack.language.alphabet.length)]!;
    }
  }
  throw new Error('Unable to generate a permitted board within retry limits');
}
